import { SSO_PROVIDER_PLATFORM, SSO_PROVIDER_TYPE } from './types/ClientSSOContext'

export const OPAU_CLIENT_GROUP_NAME = 'TELECARE_OPAU'

export enum SSO_CLIENT {
  TELECARE = 'TELECARE',
  TELECARE_AUTH0 = 'TELECARE_AUTH0',
  TELECARE_JC = 'TELECARE_JC',
  TELECARE_OAM = 'TELECARE_OAM',
  TELECARE_OPAU_SCMC = 'TELECARE_OPAU_SCMC',
  TELECARE_OPAU_CHHHS = 'TELECARE_OPAU_CHHHS',
  TELECARE_OPAU_RHC = 'TELECARE_OPAU_RHC',
  TELECARE_OPAU_WBHHS = 'TELECARE_OPAU_WBHHS',
  HDA = 'HDA',
  CONSULTMED = 'CONSULTMED',
  SLHD = 'SLHD',
  EVERLAB = 'EVERLAB',
  // for everlab dev
  EVERLAB_JC = 'EVERLAB_JC',
}

interface SSOClientConfig {
  clientName: SSO_CLIENT
  identityProvider?: string
  clientId?: string
  orgId?: string
  clinicId?: string
  forcePhoneNumber?: string
  prescriberOnly?: boolean
  providerId?: string
  providerPlatform?: SSO_PROVIDER_PLATFORM
  providerType?: SSO_PROVIDER_TYPE
}

// Shared SSO clients (work in both dev and prod environments)
const sharedSSOClientConfigs: SSOClientConfig[] = [
  {
    clientName: SSO_CLIENT.HDA,
    identityProvider: process.env.REACT_APP_HDA_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_HDA_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_HDA_ORG_ID,
    clinicId: process.env.REACT_APP_HDA_CLINIC_ID,
    forcePhoneNumber: process.env.REACT_APP_HDA_PHONE_NUMBER,
    prescriberOnly: true,
    providerPlatform: SSO_PROVIDER_PLATFORM.AZURE_AD,
    providerType: SSO_PROVIDER_TYPE.SAML,
  },
  {
    clientName: SSO_CLIENT.SLHD,
    identityProvider: process.env.REACT_APP_SLHD_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_SLHD_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_SLHD_ORG_ID,
    clinicId: process.env.REACT_APP_SLHD_CLINIC_ID,
    prescriberOnly: false,
    providerPlatform: SSO_PROVIDER_PLATFORM.OAM,
    providerType: SSO_PROVIDER_TYPE.SAML,
  },
  {
    clientName: SSO_CLIENT.CONSULTMED,
    identityProvider: process.env.REACT_APP_CONSULTMED_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_CONSULTMED_COGNITO_CLIENT_ID,
    providerId: process.env.REACT_APP_CONSULTMED_TENANT_PROVIDER_ID,
    providerPlatform: SSO_PROVIDER_PLATFORM.AUTH0,
    providerType: SSO_PROVIDER_TYPE.OIDC,
  },
  {
    clientName: SSO_CLIENT.TELECARE,
    identityProvider: process.env.REACT_APP_TELECARE_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_TELECARE_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_TELECARE_ORG_ID,
    clinicId: process.env.REACT_APP_TELECARE_CLINIC_ID,
    forcePhoneNumber: process.env.REACT_APP_HDA_PHONE_NUMBER,
    prescriberOnly: true,
    providerPlatform: SSO_PROVIDER_PLATFORM.AZURE_AD,
    providerType: SSO_PROVIDER_TYPE.SAML,
  },
]

// Production-only SSO client configurations
const prodOnlySSOClientConfigs: SSOClientConfig[] = [
  {
    clientName: SSO_CLIENT.TELECARE_OPAU_CHHHS,
    identityProvider: process.env.REACT_APP_TELECARE_OPAU_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_TELECARE_OPAU_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_TELECARE_OPAU_CHHHS_ORG_ID,
    clinicId: process.env.REACT_APP_TELECARE_OPAU_CHHHS_CLINIC_ID,
    providerId: process.env.REACT_APP_TELECARE_OPAU_TENANT_PROVIDER_ID,
    prescriberOnly: true,
    providerPlatform: SSO_PROVIDER_PLATFORM.COGNITO,
    providerType: SSO_PROVIDER_TYPE.OIDC,
  },
  {
    clientName: SSO_CLIENT.TELECARE_OPAU_RHC,
    identityProvider: process.env.REACT_APP_TELECARE_OPAU_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_TELECARE_OPAU_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_TELECARE_OPAU_RHC_ORG_ID,
    clinicId: process.env.REACT_APP_TELECARE_OPAU_RHC_CLINIC_ID,
    providerId: process.env.REACT_APP_TELECARE_OPAU_TENANT_PROVIDER_ID,
    prescriberOnly: true,
    providerPlatform: SSO_PROVIDER_PLATFORM.COGNITO,
    providerType: SSO_PROVIDER_TYPE.OIDC,
  },
  {
    clientName: SSO_CLIENT.TELECARE_OPAU_WBHHS,
    identityProvider: process.env.REACT_APP_TELECARE_OPAU_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_TELECARE_OPAU_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_TELECARE_OPAU_WBHHS_ORG_ID,
    clinicId: process.env.REACT_APP_TELECARE_OPAU_WBHHS_CLINIC_ID,
    providerId: process.env.REACT_APP_TELECARE_OPAU_TENANT_PROVIDER_ID,
    prescriberOnly: true,
    providerPlatform: SSO_PROVIDER_PLATFORM.COGNITO,
    providerType: SSO_PROVIDER_TYPE.OIDC,
  },
  {
    clientName: SSO_CLIENT.EVERLAB,
    identityProvider: process.env.REACT_APP_EVERLAB_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_EVERLAB_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_EVERLAB_ORG_ID,
    prescriberOnly: false,
    clinicId: process.env.REACT_APP_EVERLAB_CLINIC_ID,
    providerPlatform: SSO_PROVIDER_PLATFORM.CLOUDFLARE,
    providerType: SSO_PROVIDER_TYPE.SAML,
  },
]

// Development/Testing-only SSO client configurations
const devOnlySSOClientConfigs: SSOClientConfig[] = [
  {
    clientName: SSO_CLIENT.TELECARE_AUTH0,
    identityProvider: process.env.REACT_APP_TELECARE_AUTH0_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_TELECARE_AUTH0_COGNITO_CLIENT_ID,
    providerId: process.env.REACT_APP_TELECARE_AUTH0_TENANT_PROVIDER_ID,
    providerPlatform: SSO_PROVIDER_PLATFORM.AUTH0,
    providerType: SSO_PROVIDER_TYPE.OIDC,
  },
  {
    clientName: SSO_CLIENT.TELECARE_JC,
    identityProvider: process.env.REACT_APP_TELECARE_JC_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_TELECARE_JC_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_TELECARE_JC_ORG_ID,
    clinicId: process.env.REACT_APP_TELECARE_JC_CLINIC_ID,
    prescriberOnly: false,
    providerPlatform: SSO_PROVIDER_PLATFORM.JUMP_CLOUD,
    providerType: SSO_PROVIDER_TYPE.SAML,
  },
  {
    clientName: SSO_CLIENT.EVERLAB_JC,
    identityProvider: process.env.REACT_APP_EVERLAB_JC_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_EVERLAB_JC_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_EVERLAB_JC_ORG_ID,
    prescriberOnly: false,
    clinicId: process.env.REACT_APP_EVERLAB_JC_CLINIC_ID,
    providerPlatform: SSO_PROVIDER_PLATFORM.JUMP_CLOUD,
    providerType: SSO_PROVIDER_TYPE.SAML,
  },
  {
    clientName: SSO_CLIENT.TELECARE_OPAU_SCMC,
    identityProvider: process.env.REACT_APP_TELECARE_OPAU_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_TELECARE_OPAU_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_TELECARE_OPAU_SCMC_ORG_ID,
    clinicId: process.env.REACT_APP_TELECARE_OPAU_SCMC_CLINIC_ID,
    providerId: process.env.REACT_APP_TELECARE_OPAU_TENANT_PROVIDER_ID,
    prescriberOnly: true,
    providerPlatform: SSO_PROVIDER_PLATFORM.COGNITO,
    providerType: SSO_PROVIDER_TYPE.OIDC,
  },
  {
    clientName: SSO_CLIENT.TELECARE_OAM,
    identityProvider: process.env.REACT_APP_TELECARE_OAM_COGNITO_IDENTITY_PROVIDER,
    clientId: process.env.REACT_APP_TELECARE_OAM_COGNITO_CLIENT_ID,
    orgId: process.env.REACT_APP_TELECARE_OAM_ORG_ID,
    clinicId: process.env.REACT_APP_TELECARE_OAM_CLINIC_ID,
    prescriberOnly: false,
    providerPlatform: SSO_PROVIDER_PLATFORM.OAM,
    providerType: SSO_PROVIDER_TYPE.SAML,
  },
]

// Export combined mappings based on environment
const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development'

export const ssoClientConfigMappings: SSOClientConfig[] = isDevelopment
  ? [...sharedSSOClientConfigs, ...devOnlySSOClientConfigs]
  : [...sharedSSOClientConfigs, ...prodOnlySSOClientConfigs]

/**
 * Resolves the current SSO client config by first trying the clientName
 * stored in clientSSOContext (unique per client), then falling back to
 * clientId lookup. This avoids ambiguity when multiple clients share
 * the same Cognito clientId (e.g. all OPAU clients).
 */
export const getSSOClientConfig = (): SSOClientConfig | undefined => {
  const contextString = localStorage.getItem('clientSSOContext')
  if (contextString) {
    try {
      const context = JSON.parse(contextString)
      if (context?.clientName) {
        const match = ssoClientConfigMappings.find((c) => c.clientName === context.clientName)
        if (match) return match
      }
    } catch {
      // fall through to clientId lookup
    }
  }
  const clientId = localStorage.getItem('client_id') || ''
  return ssoClientConfigMappings.find((client) => client.clientId === clientId)
}
